import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import lottie, {AnimationItem} from 'lottie-web';
import wheelClickAnimation from '../../../refer-earn-wheel-click.json';
import {FissionV4Open, openFissionV4Round} from '../../../fission-v4.service';
import {errorLog} from '@/utils';
import {
  CASH_GIFT_GUIDE_DISMISS_MS,
  CASH_GIFT_GUIDE_DURATION_MS,
  CASH_GIFT_SELECTION_FEEDBACK_MS,
} from '../constants';

interface UseCashGiftSelectionOptions {
  visible: boolean;
  cashGiftAwardValues: number[];
  onOpened: (nextRound?: FissionV4Open) => void;
  onCloseCash: (onClosed?: () => void) => void;
  onOpeningStart?: () => void;
  onOpeningError?: () => void;
  onBeforeClose?: (nextRound: FissionV4Open) => void | Promise<void>;
}

export type CashGiftRevealPhase =
  | 'idle'
  | 'selectedOpen'
  | 'allOpen'
  | 'focused';

export interface CashGiftRevealItem {
  amount?: number;
  opened: boolean;
}

const CASH_GIFT_SELECTED_OPEN_DELAY_MS = 1100;
const CASH_GIFT_ALL_OPEN_DELAY_MS = 1200;
const CASH_GIFT_FOCUS_DELAY_MS = 1500;

const shuffleAmounts = (values: number[]) =>
  values
    .map(value => ({value, seed: Math.random()}))
    .sort((left, right) => left.seed - right.seed)
    .map(item => item.value);

const getOpenAmount = (nextRound?: FissionV4Open) => {
  const openAmount = Number(nextRound?.openAmount ?? nextRound?.currentAmount);

  return Number.isFinite(openAmount) ? openAmount : 0;
};

const getCashGiftCandidateAmounts = (
  nextRound: FissionV4Open | undefined,
  cashGiftAwardValues: number[],
) => {
  const sourceValues =
    nextRound?.boxNumArr ||
    (nextRound?.frontAwardItems?.length === 4
      ? nextRound.frontAwardItems
      : cashGiftAwardValues);

  return sourceValues
    .slice(0, 4)
    .map(value => Number(value))
    .filter(value => Number.isFinite(value));
};

const getOtherCashGiftAmounts = (
  openAmount: number,
  candidateAmounts: number[],
) => {
  if (!candidateAmounts.length) {
    return shuffleAmounts([
      openAmount * 0.62,
      openAmount * 0.78,
      openAmount * 1.18,
    ]);
  }

  const closestIndex = candidateAmounts.reduce((closest, value, index) => {
    const closestDistance = Math.abs(candidateAmounts[closest] - openAmount);
    const currentDistance = Math.abs(value - openAmount);

    return currentDistance < closestDistance ? index : closest;
  }, 0);

  const restAmounts = candidateAmounts.filter(
    (_, index) => index !== closestIndex,
  );

  if (restAmounts.length >= 3) {
    return shuffleAmounts(restAmounts).slice(0, 3);
  }

  const fallbackAmounts = [
    ...restAmounts,
    openAmount * 0.72,
    openAmount * 0.86,
    openAmount * 1.08,
  ];

  return shuffleAmounts(fallbackAmounts).slice(0, 3);
};

const buildCashGiftRevealItems = (
  selectedIndex: number,
  openAmount: number,
  candidateAmounts: number[],
  openOnlySelected: boolean,
): CashGiftRevealItem[] => {
  const otherAmounts = getOtherCashGiftAmounts(openAmount, candidateAmounts);
  let otherIndex = 0;

  return Array.from({length: 4}, (_, index) => {
    if (index === selectedIndex) {
      return {
        amount: openAmount,
        opened: true,
      };
    }

    return {
      amount: otherAmounts[otherIndex++],
      opened: !openOnlySelected,
    };
  });
};

export function useCashGiftSelection({
  visible,
  cashGiftAwardValues,
  onOpened,
  onCloseCash,
  onOpeningStart,
  onOpeningError,
  onBeforeClose,
}: UseCashGiftSelectionOptions) {
  const [selectedCashGiftIndex, setSelectedCashGiftIndex] = useState<
    number | null
  >(null);
  const [cashGiftRevealPhase, setCashGiftRevealPhase] =
    useState<CashGiftRevealPhase>('idle');
  const [cashGiftRevealItems, setCashGiftRevealItems] = useState<
    CashGiftRevealItem[]
  >(() => Array.from({length: 4}, () => ({opened: false})));
  const [showCashGiftGuide, setShowCashGiftGuide] = useState(visible);
  const [cashGiftGuideDismissing, setCashGiftGuideDismissing] = useState(false);
  const [showCashGiftIdleEffects, setShowCashGiftIdleEffects] = useState(true);
  const cashGiftGuideRef = useRef<HTMLDivElement>(null);
  const cashGiftAnimationRef = useRef<AnimationItem | null>(null);
  const cashGiftSelectTimerRefs = useRef<number[]>([]);
  const cashGiftGuideTimerRef = useRef<number | null>(null);
  const cashGiftGuideDismissTimerRef = useRef<number | null>(null);
  const cashGiftEffectsTimerRef = useRef<number | null>(null);
  const cashGiftSelectionIdRef = useRef(0);

  useLayoutEffect(() => {
    if (!visible) {
      cashGiftSelectionIdRef.current += 1;
      if (cashGiftGuideDismissTimerRef.current != null) {
        window.clearTimeout(cashGiftGuideDismissTimerRef.current);
        cashGiftGuideDismissTimerRef.current = null;
      }
      if (cashGiftEffectsTimerRef.current != null) {
        window.clearTimeout(cashGiftEffectsTimerRef.current);
        cashGiftEffectsTimerRef.current = null;
      }
      setShowCashGiftGuide(false);
      setCashGiftGuideDismissing(false);
      setShowCashGiftIdleEffects(true);
      setCashGiftRevealPhase('idle');
      setCashGiftRevealItems(Array.from({length: 4}, () => ({opened: false})));
      return;
    }

    setShowCashGiftGuide(true);
    setCashGiftGuideDismissing(false);
    setShowCashGiftIdleEffects(true);
  }, [visible]);

  useEffect(() => {
    if (!visible || !showCashGiftGuide || !cashGiftGuideRef.current) {
      return;
    }

    cashGiftAnimationRef.current = lottie.loadAnimation({
      container: cashGiftGuideRef.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: wheelClickAnimation,
    });
    const animation = cashGiftAnimationRef.current;
    cashGiftGuideTimerRef.current = window.setTimeout(() => {
      cashGiftGuideTimerRef.current = null;
      animation?.pause();
      setCashGiftGuideDismissing(true);
      cashGiftGuideDismissTimerRef.current = window.setTimeout(() => {
        cashGiftGuideDismissTimerRef.current = null;
        animation?.destroy();
        if (cashGiftAnimationRef.current === animation) {
          cashGiftAnimationRef.current = null;
        }
        setShowCashGiftGuide(false);
        setCashGiftGuideDismissing(false);
      }, CASH_GIFT_GUIDE_DISMISS_MS);
    }, CASH_GIFT_GUIDE_DURATION_MS);

    return () => {
      if (cashGiftGuideTimerRef.current != null) {
        window.clearTimeout(cashGiftGuideTimerRef.current);
        cashGiftGuideTimerRef.current = null;
      }
      cashGiftAnimationRef.current?.destroy();
      cashGiftAnimationRef.current = null;
    };
  }, [showCashGiftGuide, visible]);

  useEffect(() => {
    return () => {
      cashGiftSelectTimerRefs.current.forEach(timer =>
        window.clearTimeout(timer),
      );
      cashGiftSelectTimerRefs.current = [];
      if (cashGiftGuideTimerRef.current != null) {
        window.clearTimeout(cashGiftGuideTimerRef.current);
      }
      if (cashGiftGuideDismissTimerRef.current != null) {
        window.clearTimeout(cashGiftGuideDismissTimerRef.current);
      }
      if (cashGiftEffectsTimerRef.current != null) {
        window.clearTimeout(cashGiftEffectsTimerRef.current);
      }
      cashGiftAnimationRef.current?.destroy();
      cashGiftAnimationRef.current = null;
    };
  }, []);

  const dismissCashGiftGuide = useCallback((selectionId: number) => {
    if (cashGiftGuideTimerRef.current != null) {
      window.clearTimeout(cashGiftGuideTimerRef.current);
      cashGiftGuideTimerRef.current = null;
    }
    if (cashGiftGuideDismissTimerRef.current != null) {
      window.clearTimeout(cashGiftGuideDismissTimerRef.current);
    }

    const animation = cashGiftAnimationRef.current;
    animation?.pause();
    setCashGiftGuideDismissing(true);
    cashGiftGuideDismissTimerRef.current = window.setTimeout(() => {
      cashGiftGuideDismissTimerRef.current = null;
      if (cashGiftSelectionIdRef.current !== selectionId) {
        return;
      }
      animation?.destroy();
      if (cashGiftAnimationRef.current === animation) {
        cashGiftAnimationRef.current = null;
      }
      setShowCashGiftGuide(false);
      setCashGiftGuideDismissing(false);
    }, CASH_GIFT_GUIDE_DISMISS_MS);
  }, []);

  const restoreCashGiftGuide = useCallback(() => {
    if (cashGiftGuideDismissTimerRef.current != null) {
      window.clearTimeout(cashGiftGuideDismissTimerRef.current);
      cashGiftGuideDismissTimerRef.current = null;
    }

    const animation = cashGiftAnimationRef.current;
    setCashGiftGuideDismissing(false);
    if (animation) {
      animation.play();
      cashGiftGuideTimerRef.current = window.setTimeout(() => {
        cashGiftGuideTimerRef.current = null;
        animation.pause();
        setCashGiftGuideDismissing(true);
        cashGiftGuideDismissTimerRef.current = window.setTimeout(() => {
          cashGiftGuideDismissTimerRef.current = null;
          animation.destroy();
          if (cashGiftAnimationRef.current === animation) {
            cashGiftAnimationRef.current = null;
          }
          setShowCashGiftGuide(false);
          setCashGiftGuideDismissing(false);
        }, CASH_GIFT_GUIDE_DISMISS_MS);
      }, CASH_GIFT_GUIDE_DURATION_MS);
    } else {
      setShowCashGiftGuide(true);
    }
  }, []);

  const closeCashGift = useCallback(() => {
    cashGiftSelectionIdRef.current += 1;
    cashGiftSelectTimerRefs.current.forEach(timer =>
      window.clearTimeout(timer),
    );
    cashGiftSelectTimerRefs.current = [];
    if (cashGiftEffectsTimerRef.current != null) {
      window.clearTimeout(cashGiftEffectsTimerRef.current);
      cashGiftEffectsTimerRef.current = null;
    }
    onCloseCash(() => {
      setSelectedCashGiftIndex(null);
      setCashGiftRevealPhase('idle');
      setCashGiftRevealItems(Array.from({length: 4}, () => ({opened: false})));
    });
  }, [onCloseCash]);

  const waitForCashGiftRevealStep = useCallback((delayMs: number) => {
    return new Promise<void>(resolve => {
      const timer = window.setTimeout(() => {
        cashGiftSelectTimerRefs.current =
          cashGiftSelectTimerRefs.current.filter(item => item !== timer);
        resolve();
      }, delayMs);

      cashGiftSelectTimerRefs.current.push(timer);
    });
  }, []);

  const chooseCashGift = useCallback(
    async (index: number) => {
      if ((showCashGiftGuide && index !== 0) || selectedCashGiftIndex != null) {
        return;
      }

      const currentSelectionId = cashGiftSelectionIdRef.current + 1;
      cashGiftSelectionIdRef.current = currentSelectionId;
      setSelectedCashGiftIndex(index);
      if (showCashGiftGuide) {
        dismissCashGiftGuide(currentSelectionId);
      }
      if (cashGiftEffectsTimerRef.current != null) {
        window.clearTimeout(cashGiftEffectsTimerRef.current);
      }
      cashGiftEffectsTimerRef.current = window.setTimeout(() => {
        cashGiftEffectsTimerRef.current = null;
        if (cashGiftSelectionIdRef.current === currentSelectionId) {
          setShowCashGiftIdleEffects(false);
        }
      }, CASH_GIFT_SELECTION_FEEDBACK_MS);

      try {
        onOpeningStart?.();
        const [nextRound] = await Promise.all([
          openFissionV4Round(),
          waitForCashGiftRevealStep(CASH_GIFT_SELECTION_FEEDBACK_MS),
        ]);

        if (cashGiftSelectionIdRef.current !== currentSelectionId) {
          return;
        }

        const openAmount = getOpenAmount(nextRound);
        const candidateAmounts = getCashGiftCandidateAmounts(
          nextRound,
          cashGiftAwardValues,
        );
        const selectedOnlyItems = buildCashGiftRevealItems(
          index,
          openAmount,
          candidateAmounts,
          true,
        );

        setCashGiftRevealPhase('selectedOpen');
        setCashGiftRevealItems(selectedOnlyItems);
        await waitForCashGiftRevealStep(CASH_GIFT_SELECTED_OPEN_DELAY_MS);

        if (cashGiftSelectionIdRef.current !== currentSelectionId) {
          return;
        }

        setCashGiftRevealPhase('allOpen');
        setCashGiftRevealItems(
          buildCashGiftRevealItems(index, openAmount, candidateAmounts, false),
        );
        const invitationReady = Promise.resolve(onBeforeClose?.(nextRound));
        await waitForCashGiftRevealStep(CASH_GIFT_ALL_OPEN_DELAY_MS);

        if (cashGiftSelectionIdRef.current !== currentSelectionId) {
          return;
        }

        setCashGiftRevealPhase('focused');
        await Promise.all([
          waitForCashGiftRevealStep(CASH_GIFT_FOCUS_DELAY_MS),
          invitationReady,
        ]);

        if (cashGiftSelectionIdRef.current !== currentSelectionId) {
          return;
        }

        onCloseCash(() => {
          setSelectedCashGiftIndex(null);
          setCashGiftRevealPhase('idle');
          setCashGiftRevealItems(
            Array.from({length: 4}, () => ({opened: false})),
          );
          onOpened(nextRound);
        });
      } catch (error) {
        errorLog('fission v4 open round failed', error);
        if (cashGiftSelectionIdRef.current === currentSelectionId) {
          cashGiftSelectionIdRef.current += 1;
          onOpeningError?.();
          if (cashGiftEffectsTimerRef.current != null) {
            window.clearTimeout(cashGiftEffectsTimerRef.current);
            cashGiftEffectsTimerRef.current = null;
          }
          setShowCashGiftIdleEffects(true);
          restoreCashGiftGuide();
          setSelectedCashGiftIndex(null);
          setCashGiftRevealPhase('idle');
          setCashGiftRevealItems(
            Array.from({length: 4}, () => ({opened: false})),
          );
        }
      }
    },
    [
      cashGiftAwardValues,
      dismissCashGiftGuide,
      onCloseCash,
      onOpeningError,
      onOpeningStart,
      onBeforeClose,
      onOpened,
      restoreCashGiftGuide,
      selectedCashGiftIndex,
      showCashGiftGuide,
      waitForCashGiftRevealStep,
    ],
  );

  return {
    selectedCashGiftIndex,
    cashGiftRevealPhase,
    cashGiftRevealItems,
    showCashGiftIdleEffects,
    cashGiftGuideRef,
    showCashGiftGuide,
    cashGiftGuideDismissing,
    closeCashGift,
    chooseCashGift,
  };
}
